import { useState, useEffect, useCallback, useMemo } from 'react'
import { grantConsent, revokeConsent } from '../utils/analytics'

const COOKIE_NAME = 'storyplanner_consent'
const COOKIE_VERSION = 1

/**
 * Cookie Consent Hook
 * Manages cookie consent state and provides methods to update preferences
 */
export const useCookieConsent = () => {
  const [consentGiven, setConsentGiven] = useState(false)
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    sessionRecording: false,
    marketing: false
  })
  const [isLoading, setIsLoading] = useState(true)
  const [showBanner, setShowBanner] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)

  // Load consent from localStorage on mount
  useEffect(() => {
    const loadConsent = () => {
      try {
        const stored = localStorage.getItem('cookieConsent')
        if (stored) {
          const parsed = JSON.parse(stored)
          setPreferences({
            essential: true, // Always true
            analytics: parsed.analytics || false,
            sessionRecording: parsed.sessionRecording || false,
            marketing: parsed.marketing || false
          })
          setConsentGiven(true)
          setShowBanner(false)
        } else {
          // No consent given yet - show banner
          setShowBanner(true)
        }
      } catch (error) {
        console.error('Error loading cookie consent:', error)
        setShowBanner(true)
      } finally {
        setIsLoading(false)
      }
    }

    loadConsent()
  }, [])

  // Update analytics services based on consent
  useEffect(() => {
    if (!isLoading && consentGiven) {
      // Update all analytics platforms with granular consent
      if (preferences.analytics || preferences.marketing) {
        grantConsent({
          analytics: preferences.analytics,
          marketing: preferences.marketing
        })
      } else {
        revokeConsent()
      }
    }
  }, [preferences, isLoading, consentGiven])

  // Update preferences
  const updatePreferences = useCallback((newPreferences) => {
    const updatedPreferences = {
      ...newPreferences,
      essential: true, // Always ensure essential is true
      timestamp: new Date().toISOString()
    }

    localStorage.setItem('cookieConsent', JSON.stringify(updatedPreferences))
    setPreferences({
      essential: updatedPreferences.essential,
      analytics: updatedPreferences.analytics,
      sessionRecording: updatedPreferences.sessionRecording || false,
      marketing: updatedPreferences.marketing
    })
    setConsentGiven(true)
    setShowBanner(false)
    setShowPreferences(false)

    // Trigger analytics events
    if (window.gtag) {
      window.gtag('event', 'cookie_consent_updated', {
        analytics: updatedPreferences.analytics,
        marketing: updatedPreferences.marketing
      })
    }
  }, [])

  // Accept all cookies
  const acceptAll = useCallback(() => {
    updatePreferences({
      essential: true,
      analytics: true,
      sessionRecording: true,
      marketing: true
    })
  }, [updatePreferences])

  // Reject all non-essential cookies
  const rejectAll = useCallback(() => {
    updatePreferences({
      essential: true,
      analytics: false,
      sessionRecording: false,
      marketing: false
    })
  }, [updatePreferences])

  // Reset consent (for testing or user request)
  const resetConsent = useCallback(() => {
    localStorage.removeItem('cookieConsent')
    setConsentGiven(false)
    setPreferences({
      essential: true,
      analytics: false,
      sessionRecording: false,
      marketing: false
    })

    // Reset Google Analytics consent
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied'
      })
    }
  }, [])

  // Open preferences modal
  const openPreferences = useCallback(() => {
    setShowBanner(false)
    setShowPreferences(true)
  }, [])

  // Close preferences modal
  const closePreferences = useCallback(() => {
    setShowPreferences(false)
    // If user hasn't responded yet, show banner again
    if (!consentGiven) {
      setShowBanner(true)
    }
  }, [consentGiven])

  // Check if a specific cookie category is allowed
  const isAllowed = useCallback((category) => {
    return preferences[category] || false
  }, [preferences])

  // Memoize the return value to prevent unnecessary re-renders
  // This ensures consuming components don't re-render just because
  // a new object is created on every hook call
  return useMemo(() => ({
    consentGiven,
    preferences,
    isLoading,
    showBanner,
    showPreferences,
    updatePreferences,
    acceptAll,
    rejectAll,
    resetConsent,
    openPreferences,
    closePreferences,
    isAllowed
  }), [
    consentGiven,
    preferences,
    isLoading,
    showBanner,
    showPreferences,
    updatePreferences,
    acceptAll,
    rejectAll,
    resetConsent,
    openPreferences,
    closePreferences,
    isAllowed
  ])
}

export default useCookieConsent